/**
 * Industry Comparison Bar Component
 *
 * Horizontal bar chart comparing user's score to industry baselines:
 * - User's score marker
 * - Industry median (p50)
 * - P25, P75, P90 percentile markers
 * - Visual delta from baseline
 * - Percentile rank badge
 *
 * Design: Data-rich visualization with clear benchmarking
 */

import React from 'react';

// ============================================================================
// TYPES
// ============================================================================

interface IndustryComparisonBarProps {
  yourScore: number;          // 0-100
  industry: string;           // 'pharma', 'academic', 'biotech', etc.
  segment: string;            // 'mid_stage', 'top_tier', etc.
  baseline: {
    median: number;           // p50
    p25: number;
    p75: number;
    p90: number;
  };
  percentileRank: 'bottom_25' | 'below_median' | 'above_median' | 'top_25' | 'top_10';
  deltaFromBaseline: number;
}

// ============================================================================
// HELPERS
// ============================================================================

const PERCENTILE_META: Record<string, { label: string; color: string; emoji: string }> = {
  top_10: { label: 'Top 10%', color: '#27AE60', emoji: '🌟' },
  top_25: { label: 'Top 25%', color: '#3498DB', emoji: '⭐' },
  above_median: { label: 'Above Median', color: '#F39C12', emoji: '👍' },
  below_median: { label: 'Below Median', color: '#E67E22', emoji: '💪' },
  bottom_25: { label: 'Bottom 25%', color: '#E74C3C', emoji: '🔻' }
};

function formatIndustryLabel(industry: string, segment: string): string {
  const industryMap: Record<string, string> = {
    pharma: 'Pharma',
    academic: 'Academic',
    biotech: 'Biotech',
    repository: 'Repository'
  };

  const segmentMap: Record<string, string> = {
    early_stage: 'Early Stage',
    mid_stage: 'Mid Stage',
    top_tier: 'Top Tier',
    research_university: 'Research',
    startup: 'Startup',
    institutional: 'Institutional'
  };

  return `${industryMap[industry] || industry} - ${segmentMap[segment] || segment}`;
}

// ============================================================================
// COMPONENT
// ============================================================================

export const IndustryComparisonBar: React.FC<IndustryComparisonBarProps> = ({
  yourScore,
  industry,
  segment,
  baseline,
  percentileRank,
  deltaFromBaseline
}) => {
  const percentileMeta = PERCENTILE_META[percentileRank];
  const industryLabel = formatIndustryLabel(industry, segment);

  return (
    <div className="industry-comparison-bar">
      {/* Header */}
      <div className="comparison-header">
        <h3>Industry Benchmark</h3>
        <div className="industry-label">{industryLabel}</div>
      </div>

      {/* Percentile Rank Badge */}
      <div className="percentile-badge" style={{ background: percentileMeta.color }}>
        <span className="badge-emoji">{percentileMeta.emoji}</span>
        <span className="badge-text">{percentileMeta.label}</span>
      </div>

      {/* Delta Display */}
      <div className="delta-display">
        <div className="delta-label">vs. Industry Median</div>
        <div className={`delta-value ${deltaFromBaseline >= 0 ? 'positive' : 'negative'}`}>
          {deltaFromBaseline >= 0 ? '+' : ''}{deltaFromBaseline.toFixed(1)} pts
        </div>
      </div>

      {/* Comparison Bar */}
      <div className="comparison-bar-container">
        {/* Background Scale */}
        <div className="bar-scale">
          <div className="scale-segment p25" style={{ width: '25%' }} />
          <div className="scale-segment median" style={{ width: '25%' }} />
          <div className="scale-segment p75" style={{ width: '25%' }} />
          <div className="scale-segment p90" style={{ width: '15%' }} />
          <div className="scale-segment top" style={{ width: '10%' }} />
        </div>

        {/* Percentile Markers */}
        <div className="percentile-markers">
          <div className="marker p25" style={{ left: '25%' }}>
            <div className="marker-line" />
            <div className="marker-label">P25<br />{baseline.p25}</div>
          </div>
          <div className="marker median" style={{ left: '50%' }}>
            <div className="marker-line median-line" />
            <div className="marker-label">Median<br />{baseline.median}</div>
          </div>
          <div className="marker p75" style={{ left: '75%' }}>
            <div className="marker-line" />
            <div className="marker-label">P75<br />{baseline.p75}</div>
          </div>
          <div className="marker p90" style={{ left: '90%' }}>
            <div className="marker-line" />
            <div className="marker-label">P90<br />{baseline.p90}</div>
          </div>
        </div>

        {/* Your Score Marker */}
        <div className="your-score-marker" style={{ left: `${yourScore}%` }}>
          <div className="score-indicator" style={{ background: percentileMeta.color }}>
            <div className="score-arrow">▼</div>
            <div className="score-value">{yourScore.toFixed(1)}</div>
            <div className="score-label">You</div>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="comparison-legend">
        <div className="legend-item">
          <div className="legend-dot" style={{ background: '#E74C3C' }} />
          <span>Bottom 25%</span>
        </div>
        <div className="legend-item">
          <div className="legend-dot" style={{ background: '#E67E22' }} />
          <span>Below Median</span>
        </div>
        <div className="legend-item">
          <div className="legend-dot" style={{ background: '#F39C12' }} />
          <span>Above Median</span>
        </div>
        <div className="legend-item">
          <div className="legend-dot" style={{ background: '#3498DB' }} />
          <span>Top 25%</span>
        </div>
        <div className="legend-item">
          <div className="legend-dot" style={{ background: '#27AE60' }} />
          <span>Top 10%</span>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// STYLES (CSS - Add to styles.css)
// ============================================================================

/*
.industry-comparison-bar {
  padding: 2rem;
  background: linear-gradient(135deg, #0f1730 0%, #1a2844 100%);
  border: 1px solid #1c2a54;
  border-radius: 12px;
  margin: 2rem 0;
}

.comparison-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.comparison-header h3 {
  margin: 0;
  font-size: 1.5rem;
  color: #e8eefc;
}

.industry-label {
  font-size: 0.875rem;
  color: #8ea1c7;
  font-weight: 500;
}

.percentile-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  color: white;
  font-weight: 600;
  margin-bottom: 1rem;
}

.delta-display {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: rgba(15, 23, 48, 0.5);
  border-radius: 8px;
  margin-bottom: 2rem;
}

.delta-label {
  font-size: 0.875rem;
  color: #8ea1c7;
}

.delta-value {
  font-size: 1.25rem;
  font-weight: 700;
}

.delta-value.positive {
  color: #27AE60;
}

.delta-value.negative {
  color: #E74C3C;
}

.comparison-bar-container {
  position: relative;
  height: 120px;
  margin: 2rem 0;
}

.bar-scale {
  display: flex;
  height: 40px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #1c2a54;
}

.scale-segment {
  height: 100%;
}

.scale-segment.p25 {
  background: linear-gradient(90deg, #E74C3C, #E67E22);
}

.scale-segment.median {
  background: linear-gradient(90deg, #E67E22, #F39C12);
}

.scale-segment.p75 {
  background: linear-gradient(90deg, #F39C12, #3498DB);
}

.scale-segment.p90 {
  background: linear-gradient(90deg, #3498DB, #27AE60);
}

.scale-segment.top {
  background: #27AE60;
}

.percentile-markers {
  position: relative;
  height: 40px;
}

.marker {
  position: absolute;
  transform: translateX(-50%);
  text-align: center;
}

.marker-line {
  width: 2px;
  height: 50px;
  background: rgba(255, 255, 255, 0.3);
  margin: 0 auto;
}

.marker-line.median-line {
  width: 3px;
  background: rgba(255, 255, 255, 0.6);
}

.marker-label {
  font-size: 0.75rem;
  color: #8ea1c7;
  margin-top: 0.25rem;
  line-height: 1.2;
}

.your-score-marker {
  position: absolute;
  top: -10px;
  transform: translateX(-50%);
  z-index: 10;
}

.score-indicator {
  padding: 0.75rem 1rem;
  border-radius: 8px;
  text-align: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  border: 2px solid white;
}

.score-arrow {
  font-size: 1.5rem;
  line-height: 1;
  color: white;
}

.score-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: white;
  margin: 0.25rem 0;
}

.score-label {
  font-size: 0.75rem;
  color: white;
  font-weight: 600;
}

.comparison-legend {
  display: flex;
  justify-content: center;
  gap: 1.5rem;
  margin-top: 1.5rem;
  flex-wrap: wrap;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: #8ea1c7;
}

.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}
*/
